import { useState, useCallback, useRef, useEffect } from "react";

interface OptiSignsConnectResult {
  accessToken: string;
  user: { name: string; email: string };
}

export function useOptiSignsConnect() {
  const [isConnecting, setIsConnecting] = useState(false);
  const resolveRef = useRef<
    ((value: OptiSignsConnectResult | null) => void) | null
  >(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const popupRef = useRef<Window | null>(null);
  const popupCheckIntervalRef = useRef<number | null>(null);

  const connectAndGetToken =
    useCallback((): Promise<OptiSignsConnectResult | null> => {
      return new Promise((resolve) => {
        resolveRef.current = resolve;
        setIsConnecting(true);

        const scioUrl =
          import.meta.env.VITE_OPTISIGNS_URL || "https://qa2.optisigns.com";
        const origin = window.location.origin;
        const popupUrl = `${scioUrl}/signIn/optidev?origin=${encodeURIComponent(origin)}`;

        // Calculate popup position (centered on screen)
        const width = 500;
        const height = 600;
        const left = Math.round(
          window.screenLeft + (window.outerWidth - width) / 2,
        );
        const top = Math.round(
          window.screenTop + (window.outerHeight - height) / 2,
        );

        // Open popup window
        const popup = window.open(
          popupUrl,
          "optisigns-connect",
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
        );

        if (!popup) {
          setIsConnecting(false);
          resolve(null);
          return;
        }

        popupRef.current = popup;

        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== scioUrl) return;

          try {
            const data =
              typeof event.data === "string"
                ? JSON.parse(event.data)
                : event.data;
            if (data.event === "optidevSignIn") {
              cleanup();
              setIsConnecting(false);
              if (resolveRef.current) {
                resolveRef.current(data.data as OptiSignsConnectResult);
                resolveRef.current = null;
              }
            }
          } catch {
            // Ignore parse errors
          }
        };

        const checkPopupClosed = () => {
          if (popupRef.current?.closed) {
            cleanup();
            setIsConnecting(false);
            if (resolveRef.current) {
              resolveRef.current(null);
              resolveRef.current = null;
            }
          }
        };

        popupCheckIntervalRef.current = window.setInterval(
          checkPopupClosed,
          500,
        );

        const cleanup = () => {
          window.removeEventListener("message", handleMessage);
          if (popupCheckIntervalRef.current) {
            clearInterval(popupCheckIntervalRef.current);
            popupCheckIntervalRef.current = null;
          }
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }
          popupRef.current = null;
        };

        cleanupRef.current = cleanup;
        window.addEventListener("message", handleMessage);
      });
    }, []);

  const cancelConnect = useCallback(() => {
    cleanupRef.current?.();
    setIsConnecting(false);
    if (resolveRef.current) {
      resolveRef.current(null);
      resolveRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => cleanupRef.current?.();
  }, []);

  return {
    connectAndGetToken,
    isConnecting,
    cancelConnect,
  };
}
